// SPDX-License-Identifier: MIT
import * as z from "zod/mini";
import { font, rgba } from '../brand.ts';
import { inCubic, outExpo } from '../core/ease.ts';
import { clamp, smoothstep } from '../core/math.ts';
import { valueNoise2 } from '../core/noise.ts';
import { DESIGN_H, DESIGN_W, defineScene } from '../scene.ts';
import { chars } from '../type.ts';
/**
 * Numbers roll in like a slot machine, one item per segment, over a beat-pulsing dot matrix.
 * Items never overlap: the outgoing item clears before the next one starts rolling.
 */
export default defineScene({
    id: 'slot-numbers',
    version: '0.1.0',
    aspects: ['16:9'],
    propsSchema: z.object({
        items: z
            .array(z.object({ value: z.string().check(z.maxLength(12)), label: z.string().check(z.maxLength(40)) })).check(z.minLength(1)).check(z.maxLength(4)),
    }),
    glyphs: (p) => p.items.map((i) => i.value + i.label).join('') + '0123456789',
    motion: () => 0.6,
    audioEvents: (p) => p.items.flatMap((_, i) => {
        const seg = 4 / p.items.length;
        return [0.1, 0.35, 0.6, 0.85].map((k) => ({
            beat: i * seg + k * Math.min(1.3, seg * 0.6),
            sfx: 'tick' as const,
            gain: 1 - k * 0.5,
        }));
    }),
    draw(ctx, props, env) {
        const { colors } = env.brand;
        const b = env.localBeat;
        ctx.fillStyle = colors.ink;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        // Dot matrix breathing with the beat.
        const pulse = 1 - smoothstep(0, 0.5, b % 1);
        ctx.fillStyle = rgba(colors.primary, 0.55);
        for (let gy = 0; gy < 27; gy++) {
            for (let gx = 0; gx < 48; gx++) {
                const nz = valueNoise2(gx * 0.18 + env.local * 0.6, gy * 0.18, 3);
                const r = 1.2 + 3.2 * nz * (0.4 + 0.6 * pulse);
                ctx.fillRect(20 + gx * 40 - r / 2, 20 + gy * 40 - r / 2, r, r);
            }
        }
        const total = env.dur / env.spb;
        const seg = total / props.items.length;
        const idx = Math.min(props.items.length - 1, Math.floor(b / seg));
        const item = props.items[idx]!;
        const lb = b - idx * seg;
        const exit = idx < props.items.length - 1 ? inCubic(smoothstep(seg - 0.3, seg, lb)) : 0;
        ctx.font = font(env.brand, 800, 210, 'mono');
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        const glyphs = chars(item.value);
        const widths = glyphs.map((g) => ctx.measureText(g).width);
        const totalW = widths.reduce((a, c) => a + c, 0);
        let x = DESIGN_W / 2 - totalW / 2;
        const cy = DESIGN_H / 2 - 40;
        const lineH = 230;
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, cy - lineH / 2, DESIGN_W, lineH);
        ctx.clip();
        glyphs.forEach((g, i) => {
            const w = widths[i]!;
            const digit = g >= '0' && g <= '9' ? g.charCodeAt(0) - 48 : -1;
            ctx.fillStyle = colors.paper;
            if (digit < 0) {
                ctx.globalAlpha = smoothstep(0.2, 0.6, lb) * (1 - exit);
                ctx.fillText(g, x, cy - exit * lineH);
            }
            else {
                // Each reel spins through 2 full turns + the target digit, staggered left to right.
                const k = outExpo(clamp((lb - 0.1 - i * 0.08) / 1.3));
                const pos = k * (20 + digit);
                ctx.globalAlpha = 1;
                for (let j = Math.floor(pos) - 1; j <= Math.floor(pos) + 1; j++) {
                    if (j < 0)
                        continue;
                    const yy = cy + (pos - j) * lineH - exit * lineH;
                    ctx.fillText(String(j % 10), x, yy);
                }
            }
            x += w;
        });
        ctx.restore();
        ctx.globalAlpha = 1;
        env.report?.text(`value-${idx}`, [DESIGN_W / 2 - totalW / 2, cy - lineH / 2, totalW, lineH], 1 - exit);
        ctx.font = font(env.brand, 500, 40);
        ctx.textAlign = 'center';
        ctx.fillStyle = rgba(colors.paper, 0.75 * smoothstep(0.6, 1.0, lb) * (1 - exit));
        ctx.fillText(item.label, DESIGN_W / 2, cy + 190);
    },
});
